import styled from '@emotion/styled';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useParams } from 'react-router';
import { z } from 'zod';
import { Button } from '../../../components/Buttons/Button';
import ReactSelect from '../../../components/Dropdown/ReactSelect';
import Label from '../../../components/Forms/Label';
import { style } from '../../../components/Shared/Style';
import { L } from '../../../lib/i18n';
import { useAddMemberToProperty } from '../hooks/useAddMembership';
import {
    useGetOrganisationProjects,
    useGetOrganisationPropertiesByProject,
    useGetOrganisations,
} from '../hooks/useGetOrganisations';

const DropDownLabel = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: style.fontSize.milli,
});

const ButtonWrapper = styled.div({
    padding: '1rem 0px 0px 0px',
    display: 'flex',
    width: '50%',
    right: '2rem',
    justifyContent: 'space-between',
    position: 'absolute',
    bottom: '1rem',
});

const addMemberPropertyForm = z.object({
    organisation: z.number(),
    project_id: z.number(),
    property_id: z.number(),
    role: z.string(),
});

type Props = {
    handleModal: () => void;
};

type AddMemberToPropertySchema = z.infer<typeof addMemberPropertyForm>;

const AddNewMemberToProperty = ({ handleModal }: Props) => {
    const { id } = useParams<{ id: string }>();
    const { mutate: addMemberToProperty } = useAddMemberToProperty();
    const {
        handleSubmit: propertySubmit,
        control: propertyControl,
        watch,
    } = useForm<AddMemberToPropertySchema>({
        resolver: zodResolver(addMemberPropertyForm),
    });
    const { data: accounts } = useGetOrganisations();
    const options = accounts?.map((account) => ({
        value: account.id,
        label: (
            <DropDownLabel>
                <span>{account.name}</span>
            </DropDownLabel>
        ),
    }));

    const { data: projectList } = useGetOrganisationProjects(watch('organisation'));

    const { data: propertyList } = useGetOrganisationPropertiesByProject(watch('project_id'));

    const projectOptions = projectList?.map((project) => ({
        value: project.id,
        label: (
            <DropDownLabel>
                <span>{project.name}</span>
            </DropDownLabel>
        ),
    }));

    const propertyOptions = propertyList?.map((property) => ({
        value: property.id,
        label: (
            <DropDownLabel>
                <span>{property.name}</span>
            </DropDownLabel>
        ),
    }));

    const roleOptions = [
        {
            value: 'user',
            label: (
                <DropDownLabel>
                    <span>{L('user')}</span>
                </DropDownLabel>
            ),
        },
    ];

    const handlePropertySubmit = (data: { property_id: number }) => {
        addMemberToProperty(
            {
                property_id: data.property_id,
                user_id: +id,
            },
            {
                onSuccess: () => {
                    handleModal();
                    toast.success(L('member_added'));
                },
            }
        );
    };

    return (
        <>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    propertySubmit(handlePropertySubmit)();
                }}
            >
                <Label title={`${L('organisation')}*`}>
                    <Controller
                        name="organisation"
                        control={propertyControl}
                        render={({ field }) => {
                            return (
                                <ReactSelect
                                    options={options || []}
                                    value={options?.find((option) => option.value === field.value)}
                                    onChange={(value) => field.onChange(value?.value || 0)}
                                />
                            );
                        }}
                    />
                </Label>
                <Label title={`${L('project')}*`}>
                    <Controller
                        name="project_id"
                        control={propertyControl}
                        render={({ field }) => {
                            return (
                                <ReactSelect
                                    options={projectOptions || []}
                                    value={projectOptions?.find((option) => option.value === field.value)}
                                    onChange={(value) => field.onChange(value?.value || 0)}
                                />
                            );
                        }}
                    />
                </Label>
                <Label title={`${L('property')}*`}>
                    <Controller
                        name="property_id"
                        control={propertyControl}
                        render={({ field }) => {
                            return (
                                <ReactSelect
                                    options={propertyOptions || []}
                                    value={propertyOptions?.find((option) => option.value === field.value)}
                                    onChange={(value) => field.onChange(value?.value || 0)}
                                />
                            );
                        }}
                    />
                </Label>
                <Label title={`${L('role')}`}>
                    <Controller
                        name="role"
                        control={propertyControl}
                        render={({ field }) => {
                            return (
                                <ReactSelect
                                    options={roleOptions || []}
                                    value={roleOptions?.find((option) => option.value === field.value)}
                                    onChange={(value) => field.onChange(value?.value || '')}
                                />
                            );
                        }}
                    />
                </Label>
                <ButtonWrapper>
                    <Button type="button" onClick={() => handleModal()} variant="primary">
                        Avbryt
                    </Button>
                    <Button type={'submit'} variant="filledPrimary">
                        {L('save')}
                    </Button>
                </ButtonWrapper>
            </form>
        </>
    );
};

export default AddNewMemberToProperty;
